import { useState, useEffect } from "react";
import emailInvitationService from "../services/email-invitation.service";
import type {
  EmailInvitation,
  EmailInvitationListResponse,
  EmailInvitationStatus,
} from "../types/email-invitation.types";

interface InvitationListViewProps {
  className?: string;
}

export const InvitationListView: React.FC<InvitationListViewProps> = ({ className = "" }) => {
  const [data, setData] = useState<EmailInvitationListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState<EmailInvitationStatus | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const loadInvitations = async () => {
    try {
      setIsLoading(true);
      setError("");
      const response = await emailInvitationService.getMyInvitations({
        status: statusFilter,
        page: currentPage,
        limit: itemsPerPage,
      });
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load invitations");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInvitations();
  }, [statusFilter, currentPage]);

  const totalPages = data ? Math.ceil(data.total / itemsPerPage) : 0;
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, data?.total || 0);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getStatusBadge = (status: EmailInvitationStatus) => {
    const styles: Record<EmailInvitationStatus, string> = {
      PENDING: "bg-gray-100 text-gray-800",
      SENT: "bg-blue-100 text-blue-800",
      FAILED: "bg-red-100 text-red-800",
      OPENED: "bg-yellow-100 text-yellow-800",
      REGISTERED: "bg-green-100 text-green-800",
      STEP_3_ONBOARDING_DOCS_SIGNED: "bg-purple-100 text-purple-800",
      STEP_4_TRAINING_ATTENDED: "bg-orange-100 text-orange-800",
      STEP_5_TEST_PASSED: "bg-cyan-100 text-cyan-800",
      STEP_6_READY_TO_SELL: "bg-emerald-100 text-emerald-800",
      STEP_7_FIRST_SALE: "bg-teal-100 text-teal-800",
      STEP_8_FIRST_PAYCHECK: "bg-green-100 text-green-800",
    };

    const labels: Record<EmailInvitationStatus, string> = {
      PENDING: "Pending",
      SENT: "Unread",
      FAILED: "Failed",
      OPENED: "Viewed",
      REGISTERED: "Registered",
      STEP_3_ONBOARDING_DOCS_SIGNED: "Docs Signed",
      STEP_4_TRAINING_ATTENDED: "Training Done",
      STEP_5_TEST_PASSED: "Test Passed",
      STEP_6_READY_TO_SELL: "Ready To Sell",
      STEP_7_FIRST_SALE: "First Sale",
      STEP_8_FIRST_PAYCHECK: "First Paycheck",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || "bg-gray-100 text-gray-800"}`}
      >
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      {/* Header with Stats */}
      <div className="p-6 border-b">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Email Invitations</h2>

        {/* Stats Summary */}
        {data && (
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{data.total}</div>
              <div className="text-xs text-gray-600">Total</div>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-900">{data.sent}</div>
              <div className="text-xs text-blue-600">Sent</div>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-900">{data.opened}</div>
              <div className="text-xs text-yellow-600">Viewed</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-900">{data.registered}</div>
              <div className="text-xs text-green-600">Registered</div>
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Filter by status:</label>
          <select
            value={statusFilter || ""}
            onChange={(e) => {
              setStatusFilter(e.target.value as EmailInvitationStatus || undefined);
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All</option>
            <option value="PENDING">Pending</option>
            <option value="SENT">Unread Invites</option>
            <option value="OPENED">Viewed Invites</option>
            <option value="REGISTERED">Registered</option>
            <option value="STEP_3_ONBOARDING_DOCS_SIGNED">Docs Signed</option>
            <option value="STEP_4_TRAINING_ATTENDED">Training Done</option>
            <option value="STEP_5_TEST_PASSED">Test Passed</option>
            <option value="STEP_6_READY_TO_SELL">Ready To Sell</option>
            <option value="STEP_7_FIRST_SALE">First Sale</option>
            <option value="STEP_8_FIRST_PAYCHECK">First Paycheck</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sent At
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Viewed At
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Registered At
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data?.invitations.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                  No invitations found
                </td>
              </tr>
            ) : (
              data?.invitations.map((invitation) => (
                <tr key={invitation.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {invitation.recipientEmail}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(invitation.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(invitation.sentAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(invitation.openedAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(invitation.registeredAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      {data && data.total > 0 && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing <span className="font-medium">{startIndex}</span> to{" "}
            <span className="font-medium">{endIndex}</span> of{" "}
            <span className="font-medium">{data.total}</span> invitations
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${currentPage === 1
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                }`}
            >
              ← Previous
            </button>

            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-600">Page</span>
              <input
                type="number"
                min="1"
                max={totalPages}
                value={currentPage}
                onChange={(e) => {
                  const page = parseInt(e.target.value) || 1;
                  if (page >= 1 && page <= totalPages) {
                    setCurrentPage(page);
                  }
                }}
                className="w-12 px-2 py-1 border border-gray-300 rounded-md text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">of {totalPages}</span>
            </div>

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${currentPage === totalPages
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                }`}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
